import React, {
  useEffect,
  useState,
} from 'react';
import { useSelector } from 'react-redux';
import { PopulateUIProvider } from '@populateui/core';
import {
  Questionnaire,
  QuestionnaireAnswer,
  QuestionnaireQuestion,
} from '@populateui/ui';
import HeaderLogo from 'components/common/headerLogo';
import QuestionnairePageHeader, {
  fixedQuestionnairePageClasses,
} from 'components/common/QuestionnairePageHeader';
import Spinner from 'components/spinner/Spinner';
import CompletionMessage from 'components/common/CompletionMessage';
import useIncompleteTempoRedirect from 'hooks/useIncompleteTempoRedirect';
import useQuestionnaireScrollCompensation from 'hooks/useQuestionnaireScrollCompensation';
import { useGetTempoQuestions, usePostTempoSubmitAnswers } from 'hooks/useApi';
import { RootState } from 'store/store';
import { patientPortalLoginUrl } from 'env';
import { parseErrorMessage } from 'utils/helper';

const TEMPO_BRAND = {
  brand: {
    primary: '#54b3a7',
    primaryInvert: '#ffffff',
    primaryHover: '#4aa89c',
    primaryActive: '#439e92',
  },
};

type TempoProps = {
  onDisplayChooseScreen?: () => void;
};

function Tempo({ onDisplayChooseScreen }: TempoProps) {
  const { tempoToken } = useSelector((state: RootState) => state.appStateSettings);
  const {
    data: tempoQuestionsData,
    isLoading,
    isError,
    error,
  } = useGetTempoQuestions(tempoToken ?? '', !!tempoToken);
  const { mutateAsync: submitAnswers, isPending: isSubmitting } = usePostTempoSubmitAnswers(tempoToken ?? '');
  const { goToCommuniqueChoice, isTablet } = useIncompleteTempoRedirect();
  const [showCompletedMessage, setShowCompletedMessage] = useState(false);
  const [answeredCount, setAnsweredCount] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const [tempoQuestionsErrorMessage, setTempoQuestionsErrorMessage] = useState('');

  const handleSubmit = async (answers: QuestionnaireAnswer[]) => {
    try {
      await submitAnswers({
        answers: answers.map((answer) => ({
          questionId: answer.questionId,
          value: answer.value,
        })),
      });
      if (onDisplayChooseScreen) {
        onDisplayChooseScreen();
      } else if (isTablet) {
        goToCommuniqueChoice();
      } else {
        setShowCompletedMessage(true);
      }
    } catch (error) {
      console.error('Error submitting answers:', error);
      const errorMessage = parseErrorMessage(error, 'Error submitting answers');
      setTempoQuestionsErrorMessage(errorMessage);
    }
  };

  const questions = (tempoQuestionsData?.questions ?? []) as QuestionnaireQuestion[];

  useEffect(() => {
    if (isError) {
      const errorMessage = parseErrorMessage(error, 'Error loading questions');
      setTempoQuestionsErrorMessage(errorMessage);
    }
  }, [isError, error]);

  const showQuestionnaire = !isLoading && !showCompletedMessage && questions.length > 0;

  useQuestionnaireScrollCompensation(showQuestionnaire);

  useEffect(() => {
    setAnsweredCount(0);
    setTotalCount(questions.length);
  }, [questions.length]);

  useEffect(() => {
    document.documentElement.classList.add('rtm-page-background');
    document.body.classList.add('rtm-page-background');

    return () => {
      document.documentElement.classList.remove('rtm-page-background');
      document.body.classList.remove('rtm-page-background');
    };
  }, []);

  return (
    <PopulateUIProvider config={TEMPO_BRAND}>
      <div className={showQuestionnaire ? 'min-h-screen' : 'min-h-screen py-8'}>
        {!showQuestionnaire && <HeaderLogo />}
        {isLoading && (
          <div className="flex justify-center items-center h-[50vh]">
            <Spinner />
          </div>
        )}
        {!isLoading && (isError || (!showCompletedMessage && questions.length === 0)) && (
          <div className="flex justify-center items-center h-[50vh]">
            <p className="text-red-600">{tempoQuestionsErrorMessage}</p>
          </div>
        )}
        {!isLoading && showCompletedMessage && (
          <CompletionMessage
            title="Evaluation Complete"
            message="Your responses have been submitted for review."
            actionLabel="Go to patient portal"
            actionHref={patientPortalLoginUrl}
          />
        )}
        {showQuestionnaire && (
          <>
            <QuestionnairePageHeader
              eyebrow="Psych Screening"
              answeredCount={answeredCount}
              totalCount={totalCount}
            />
            <Questionnaire
              questions={questions}
              onSubmit={handleSubmit}
              isSubmitting={isSubmitting}
              onAnswersChange={({ answeredCount: count, totalCount: total }) => {
                setAnsweredCount(count);
                setTotalCount(total);
              }}
              submitLabel="SUBMIT"
              classes={fixedQuestionnairePageClasses}
            />
          </>
        )}
      </div>
    </PopulateUIProvider>
  );
}

export default Tempo;
